/**
 * SuccessStep - Institutional Grade Completion Screen
 *
 * Final step of QBridge Setup Wizard
 * - Celebration animation
 * - Import summary with statistics
 * - What happens next section
 * - Primary CTA to open Q AI Coach
 */

import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SyncIcon from '@mui/icons-material/Sync';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import TokenIcon from '@mui/icons-material/Token';

const SuccessStep = ({
    importedCount = 0,
    skippedCount = 0,
    tokensUsed = 0,
    mt5Account,
    mt5Broker,
    onOpenCoach,
    onClose,
    colors
}) => {
    const [showConfetti, setShowConfetti] = useState(true);

    // Hide confetti after animation
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowConfetti(false);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    // What happens next items
    const nextSteps = [
        {
            icon: <Box sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: colors.primary,
                boxShadow: `0 0 8px ${colors.primary}`
            }} />,
            text: 'QBridge is running silently in your system tray'
        },
        {
            icon: <SyncIcon sx={{ fontSize: '1rem', color: colors.info || '#3B82F6' }} />,
            text: 'New trades will sync automatically every 5 minutes'
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: '1rem', color: colors.primary }} />,
            text: 'Q AI Coach is ready to analyze your performance'
        },
        {
            icon: <ChatIcon sx={{ fontSize: '1rem', color: colors.purple || '#7c3aed' }} />,
            text: 'Ask Q anything: "How am I doing this week?"'
        }
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                py: 2,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* ═══════════════════════════════════════════════════════════════════
                CELEBRATION ANIMATION
                ═══════════════════════════════════════════════════════════════════ */}
            {showConfetti && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        zIndex: 0
                    }}
                >
                    {/* Animated particles */}
                    {[...Array(20)].map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: 'absolute',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: [colors.primary, colors.primaryLight, colors.info || '#3B82F6', colors.purple || '#7c3aed'][i % 4],
                                left: `${Math.random() * 100}%`,
                                top: '-20px',
                                animation: `confetti-fall ${2 + Math.random() * 2}s ease-out forwards`,
                                animationDelay: `${Math.random() * 0.5}s`,
                                '@keyframes confetti-fall': {
                                    '0%': {
                                        transform: 'translateY(0) rotate(0deg)',
                                        opacity: 1
                                    },
                                    '100%': {
                                        transform: `translateY(500px) rotate(${360 + Math.random() * 360}deg)`,
                                        opacity: 0
                                    }
                                }
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* ═══════════════════════════════════════════════════════════════════
                SUCCESS ICON & TITLE
                ═══════════════════════════════════════════════════════════════════ */}
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                {/* Animated Success Icon */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2.5,
                        boxShadow: `0 0 50px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.5)'}`,
                        animation: 'success-pulse 2s ease-in-out infinite',
                        '@keyframes success-pulse': {
                            '0%, 100%': {
                                boxShadow: `0 0 30px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.4)'}`
                            },
                            '50%': {
                                boxShadow: `0 0 60px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.6)'}`
                            }
                        }
                    }}
                >
                    <CheckCircleIcon sx={{ color: '#FFFFFF', fontSize: '3rem' }} />
                </Box>

                <Typography
                    sx={{
                        color: colors.text,
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        mb: 0.5
                    }}
                >
                    Setup Complete!
                </Typography>

                <Typography
                    sx={{
                        color: colors.textMuted,
                        fontSize: '1rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    Your trading history is now synced
                </Typography>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                IMPORT SUMMARY
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '480px',
                    background: colors.surface,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '12px',
                    p: 2.5,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        mb: 2,
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    Import Summary
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Imported */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ color: colors.primary, fontSize: '1.125rem' }} />
                            <Typography
                                sx={{
                                    color: colors.textSecondary,
                                    fontSize: '0.875rem',
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Trades imported
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                color: colors.primary,
                                fontSize: '1rem',
                                fontWeight: 700,
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            {importedCount.toLocaleString()}
                        </Typography>
                    </Box>

                    {/* Skipped */}
                    {skippedCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SkipNextIcon sx={{ color: colors.textMuted, fontSize: '1.125rem' }} />
                                <Typography
                                    sx={{
                                        color: colors.textMuted,
                                        fontSize: '0.875rem',
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    Duplicates skipped
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: colors.textMuted,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                {skippedCount}
                            </Typography>
                        </Box>
                    )}

                    {/* Tokens */}
                    {tokensUsed > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TokenIcon sx={{ color: colors.info || '#3B82F6', fontSize: '1.125rem' }} />
                                <Typography
                                    sx={{
                                        color: colors.textSecondary,
                                        fontSize: '0.875rem',
                                        fontFamily: "'Inter', sans-serif"
                                    }}
                                >
                                    Tokens used
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: colors.info || '#3B82F6',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                {tokensUsed}
                            </Typography>
                        </Box>
                    )}

                    {/* Auto-sync status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SyncIcon sx={{ color: colors.primary, fontSize: '1.125rem' }} />
                            <Typography
                                sx={{
                                    color: colors.textSecondary,
                                    fontSize: '0.875rem',
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Auto-sync
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                px: 1.5,
                                py: 0.25,
                                borderRadius: '4px',
                                background: colors.primaryBg || 'rgba(39, 181, 109, 0.1)'
                            }}
                        >
                            <Typography
                                sx={{
                                    color: colors.primary,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                ENABLED
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Account info */}
                {(mt5Account || mt5Broker) && (
                    <Box
                        sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: `1px solid ${colors.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.textDim,
                                fontSize: '0.75rem',
                                fontFamily: "'Inter', sans-serif"
                            }}
                        >
                            Account: {mt5Account} @ {mt5Broker}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                WHAT HAPPENS NEXT
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '480px',
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    p: 2.5,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Typography
                    sx={{
                        color: colors.textSecondary,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        mb: 2,
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    What Happens Next
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {nextSteps.map((step, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                            }}
                        >
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '8px',
                                    background: colors.background,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                {step.icon}
                            </Box>
                            <Typography
                                sx={{
                                    color: colors.textSecondary,
                                    fontSize: '0.875rem',
                                    fontFamily: "'Inter', sans-serif",
                                    lineHeight: 1.4
                                }}
                            >
                                {step.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                ACTION BUTTONS
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '480px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                {/* Primary CTA */}
                <Button
                    fullWidth
                    onClick={onOpenCoach}
                    startIcon={<RocketLaunchIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                        py: 1.75,
                        px: 4,
                        borderRadius: '10px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'none',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                        color: '#FFFFFF',
                        boxShadow: `0 4px 20px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.3)'}`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
                            boxShadow: `0 6px 24px ${colors.primaryGlow || 'rgba(39, 181, 109, 0.4)'}`
                        }
                    }}
                >
                    Open Q AI Coach
                </Button>

                {/* Secondary action */}
                <Button
                    fullWidth
                    onClick={onClose}
                    sx={{
                        py: 1.25,
                        px: 4,
                        borderRadius: '10px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'none',
                        color: colors.textMuted,
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        '&:hover': {
                            background: colors.surfaceHover,
                            borderColor: colors.borderLight
                        }
                    }}
                >
                    Close Setup
                </Button>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                FOOTER BRANDING
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontSize: '0.75rem',
                            fontWeight: 800,
                            fontFamily: "'Inter', sans-serif"
                        }}
                    >
                        Q
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        color: colors.textDim,
                        fontSize: '0.75rem',
                        fontFamily: "'Inter', sans-serif"
                    }}
                >
                    Q AI Coach — Your AI Trading Coach
                </Typography>
            </Box>
        </Box>
    );
};

export default SuccessStep;
